import type {
  AiChatStateResponse,
  AiRequest,
  AiResponse,
  AiTask,
} from '@store/reducers/ai/types'
import type { AxiosResponse, CancelTokenSource } from 'axios'
import type { SagaIterator } from 'redux-saga'
import { call, put, take } from 'redux-saga/effects'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { AIExternalActions } from '../../actions'
import { fetchAIChatState } from '../performRequest'
import { FINAL_TASK_STATUSES } from './constants'
import { getClarifyDataFromTask, normalizeTaskResult } from './parseTask'
import { createTaskStatusChannel } from './taskStatusChannel'
import type { TaskStreamEvent } from './types'

export function* waitForTaskCompletion(
  task: AiTask
): SagaIterator<AiResponse | null> {
  const taskStatusChannel = yield call(createTaskStatusChannel, task)
  let streamedAssistantMessage = ''

  try {
    while (true) {
      const event: TaskStreamEvent = yield take(taskStatusChannel)

      if (event.type === 'cancelled') {
        return null
      }

      if (event.type === 'stream_error') {
        throw event.error
      }

      if (event.type === 'message') {
        if (event.message.role === 'assistant' && event.message.content) {
          streamedAssistantMessage = event.message.content
        }
        continue
      }

      const currentTask = event.task

      if (currentTask.status === 'AWAITING_USER') {
        const clarifyData = getClarifyDataFromTask(currentTask)

        if (clarifyData) {
          yield put(
            AIExternalActions.addMessage({
              message: {
                type: 'clarify',
                text: clarifyData.question,
                options: clarifyData.options,
              },
            })
          )
          sendGtmEvent(GtmTriggerName.AI_CHAT_RESPONSE_NO_ACTION)
        } else if (streamedAssistantMessage) {
          yield put(
            AIExternalActions.addMessage({
              message: {
                type: 'assistant',
                text: streamedAssistantMessage,
              },
            })
          )
          sendGtmEvent(GtmTriggerName.AI_CHAT_RESPONSE_NO_ACTION)
        }

        return null
      }

      if (!FINAL_TASK_STATUSES.has(currentTask.status)) {
        continue
      }

      if (currentTask.status !== 'COMPLETED') {
        throw new Error(`AI task finished with status ${currentTask.status}`)
      }

      const normalizedResult = normalizeTaskResult(currentTask.result)
      if (!normalizedResult) {
        throw new Error('AI task completed without assistant response payload')
      }

      return normalizedResult
    }
  } finally {
    taskStatusChannel.close()
  }
}

export function* resolveTaskFromConflict(
  requestData: AiRequest,
  cancelToken: CancelTokenSource['token']
): SagaIterator<AiTask | null> {
  if (!requestData.projectId) {
    return null
  }

  const chatStateResponse: AxiosResponse<AiChatStateResponse> = yield call(
    fetchAIChatState,
    requestData.projectId,
    cancelToken
  )

  return chatStateResponse.data.activeTask
}
